import React, { useState, useEffect } from "react";
import {
	ChevronDown,
	HelpCircle,
	Loader2,
	Upload,
	FileText,
	Clock,
	XCircle,
	CheckCircle,
	Info,
} from "lucide-react";
import { useApp } from "./AppContext";
import { ProfileIntroOverlay } from "./ProfileIntroOverlay";
import {
	useGetExtra,
	useUpdateExtra,
	useGetCountries,
	useGetOccupations,
} from "../api";
import { useContracts } from "../api/contracts/useContracts";
import type { Extra, LanguageLevel, AmountFlights } from "../types/extra.d";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { toLocalDateString } from "../utils";
import { useTranslation } from "../i18n/TranslationContext";
import { COMPANY_OPTIONS } from "../constants/companies";
import { AUSWEIS_COLOR_OPTIONS } from "../constants/ausweisColors";
import { MainLayout } from "./MainLayout";
import { useOccupationProof } from "../api/occupationProof/useOccupationProof";
import { useWorkPermitProof } from "../api/workPermitProof/useWorkPermitProof";
import { useDataAccuracyConfirmation } from "../hooks/useDataAccuracyConfirmation";

import { LANGUAGE_LEVELS } from "../constants/languageLevels";

const AMOUNT_FLIGHTS: AmountFlights[] = ["0-2", "3-4", "5-8", "8+"];

export function ProfileCompletion() {
	const {
		userProfile,
		setUserProfile,
		updateUserCompletionState,
		navigateDirectly,
		canAccessScreen,
		navigationContext,
		onboardingState,
	} = useApp();

	const { t } = useTranslation();
	const { countries, loading: countriesLoading } = useGetCountries();
	const { occupations, loading: occupationsLoading } = useGetOccupations();

	// Get current user to fetch their ID and check if internal
	const { user } = useCurrentUser();

	// Fetch extra data from API with dynamic user ID
	const {
		extra,
		loading: apiLoading,
		error: apiError,
		refetch,
	} = useGetExtra(user?.extraId);

	// Hook for occupation proof
	const { proofInfo, deleteProof } = useOccupationProof(user?.extraId);

	// Hook for work permit proof
	const { proofInfo: workPermitInfo, deleteProof: deleteWorkPermitProof } =
		useWorkPermitProof(user?.extraId);

	// Hook for updating extra data
	const { updateExtra, loading: updateLoading } = useUpdateExtra();

	// Hook for checking contracts to determine if address fields should be disabled
	const { contracts, loading: contractsLoading } = useContracts();

	// Initialize form data state matching Extra interface
	const [formData, setFormData] = useState<Partial<Extra>>({
		first_name: "",
		last_name: "",
		email: "",
		mobile_number: "",
		street: "",
		postcode: "",
		city: "",
		date_of_birth: "",
		city_of_birth: "",
		age_group: undefined,
		gender: undefined,
		language_level: undefined,
		sozialversicherung: "",
		geburtsname: "",
		idnr: "",
		nationality: undefined,
		iban: "",
		bic: "",
		different_recipient: "",
		haupt_ag: undefined,
		profile_status: undefined,
		flughafenausweisnummer: "",
		company: "",
		department: "",
		ausweis_color: "",
		main_occupation_id: undefined,
		id_number: "",
		accepts_64_day_rule: false,
		confirmed_unallowed_occupation: false,
		amount_flights: undefined,
		});

	const [errors, setErrors] = useState<Record<string, string>>({});
	const [isLoadingData, setIsLoadingData] = useState(true);
	// Confirmation modal for navigation to occupation proof
	const [showNavigationWarningModal, setShowNavigationWarningModal] =
		useState(false);
	const [navigationTarget, setNavigationTarget] = useState<
		"occupation-proof" | "work-permit-proof" | null
	>(null);
	// Confirmation modal for occupation change
	const [showOccupationChangeModal, setShowOccupationChangeModal] =
		useState(false);
	const [pendingOccupationId, setPendingOccupationId] = useState<
		number | undefined
	>(undefined);
	// Confirmation modal for nationality change (work permit)
	const [showNationalityChangeModal, setShowNationalityChangeModal] =
		useState(false);
	const [pendingNationalityId, setPendingNationalityId] = useState<
		number | undefined
	>(undefined);
	// Info modal for ID number explanation
	const [showIdInfoModal, setShowIdInfoModal] = useState(false);
	// Info modal for 64-day rule explanation
	const [show64DayRuleInfoModal, setShow64DayRuleInfoModal] = useState(false);
	// Data accuracy acknowledgment with localStorage persistence
	const { acknowledgesDataAccuracy, setAcknowledgesDataAccuracy } =
		useDataAccuracyConfirmation("profile", user?.extraId);

	// Profile intro overlay state
	const [profileState, setProfileState] = useState<any>({
		hasSeenIntro: false,
		showIntroOverlay: false,
		isFirstVisit: true,
	});

	// Check if profile is complete based on backend data
	const isProfileComplete = extra?.profile_status === "ready_to_work";


	// Initialize profile state on mount
	useEffect(() => {
		const hasSeenProfileIntro = localStorage.getItem("hasSeenProfileIntro");
		const isFirstTime = !hasSeenProfileIntro;

		setProfileState({
			hasSeenIntro: !!hasSeenProfileIntro,
			isFirstVisit: isFirstTime,
			showIntroOverlay: isFirstTime,
		});
	}, []);

	// Update form data when API data is loaded
	useEffect(() => {
		if (extra && !apiLoading) {
			// Set all extra data directly to form, converting nulls to empty strings
			const sanitizedData: Partial<Extra> = {};
			const stringFields = [
				"first_name",
				"last_name",
				"email",
				"mobile_number",
				"street",
				"postcode",
				"city",
				"date_of_birth",
				"city_of_birth",
				"sozialversicherung",
				"geburtsname",
				"idnr",
				"iban",
				"bic",
				"different_recipient",
				"flughafenausweisnummer",
				"code_of_conduct",
				"company",
				"department",
				"ausweis_color",
			];

			for (const [key, value] of Object.entries(extra)) {
				// Convert null values to empty strings for string fields
				if (value === null && stringFields.includes(key)) {
					(sanitizedData as any)[key] = "";
				} else if (
					key === "language_level" &&
					(value === null || value === undefined)
				) {
					// Ensure language_level is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else if (
					key === "nationality" &&
					(value === null || value === undefined)
				) {
					// Ensure nationality is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else if (
					key === "age_group" &&
					(value === null || value === undefined)
				) {
					// Ensure age_group is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else if (
					key === "gender" &&
					(value === null || value === undefined)
				) {
					// Ensure gender is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else if (
					key === "main_occupation_id" &&
					(value === null || value === undefined)
				) {
					// Ensure main_occupation_id is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else if (
					key === "amount_flights" &&
					(value === null || value === undefined)
				) {
					// Ensure amount_flights is undefined when null/undefined from backend
					(sanitizedData as any)[key] = undefined;
				} else {
					(sanitizedData as any)[key] = value;
				}
			}

			setFormData({
				...sanitizedData,
				date_of_birth: extra.date_of_birth
					? formatDateForInput(extra.date_of_birth)
					: "",
			});

			setIsLoadingData(false);
		} else if (!apiLoading && !extra) {
			// No data from API, start with empty form
			setIsLoadingData(false);
		}
	}, [extra, apiLoading]);

	// Helper function to format date for input field
	const formatDateForInput = (dateString: string): string => {
		if (!dateString) return "";
		// Use the utility function to get local date string in YYYY-MM-DD format
		return toLocalDateString(dateString);
	};

	const handleInputChange = (field: keyof Partial<Extra>, value: any) => {
		// Special handling for occupation changes when proof exists
		if (
			field === "main_occupation_id" &&
			proofInfo &&
			value !== formData.main_occupation_id
		) {
			// If changing occupation and there's existing proof, show warning
			setPendingOccupationId(value);
			setShowOccupationChangeModal(true);
			return;
		}

		// Special handling for nationality changes when work permit exists
		if (
			field === "nationality" &&
			workPermitInfo &&
			value !== formData.nationality
		) {
			const newCountry = countries.find((c) => c.country_id === value);
			const oldCountry = countries.find(
				(c) => c.country_id === formData.nationality
			);

			// Check if changing from non-EU to EU or vice versa
			if (newCountry && oldCountry && newCountry.is_eu !== oldCountry.is_eu) {
				setPendingNationalityId(value);
				setShowNationalityChangeModal(true);
				return;
			}
		}

		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({ ...prev, [field]: "" }));
		}
	};

	const validateForm = () => {
		const newErrors: Record<string, string> = {};

		// Personal Information
		if (!formData.first_name?.trim()) {
			newErrors.first_name = t(
				"profileCompletion.validation.firstNameRequired"
			);
		}
		if (!formData.last_name?.trim()) {
			newErrors.last_name = t("profileCompletion.validation.lastNameRequired");
		}
		if (!formData.date_of_birth) {
			newErrors.date_of_birth = t(
				"profileCompletion.validation.dateOfBirthRequired"
			);
		} else {
			const dob = formData.date_of_birth;
			const isValidFormat = /^\d{4}-\d{2}-\d{2}$/.test(dob);

			if (!isValidFormat) {
				newErrors.date_of_birth =
					"Ungültiges Datumsformat. Bitte verwenden Sie das Format TT.MM.JJJJ.";
			} else if (dob > toLocalDateString(new Date())) {
				newErrors.date_of_birth = t(
					"profileCompletion.validation.dateOfBirthFuture"
				);
			} else if (dob < "1900-01-01") {
				newErrors.date_of_birth =
					"Bitte geben Sie ein gültiges Geburtsdatum ein (nach 1900).";
			}
		}
		if (!formData.nationality) {
			newErrors.nationality = t(
				"profileCompletion.validation.nationalityRequired"
			);
		}
		if (!formData.language_level) {
			newErrors.language_level = t(
				"profileCompletion.validation.germanLevelRequired"
			);
		}
		if (!formData.amount_flights) {
			newErrors.amount_flights = "Anzahl der Flüge ist erforderlich";
		}

		// ID Number - required for external users only
		if (user?.internal_extra !== true && !formData.id_number?.trim()) {
			newErrors.id_number = "ID-Nummer ist erforderliche";
		}

		// Contact Information (these are read-only but still required)
		if (user?.internal_extra && !formData.email?.trim()) {
			newErrors.email = t("profileCompletion.validation.emailRequired");
		}
		if (!formData.mobile_number?.trim()) {
			newErrors.mobile_number = t(
				"profileCompletion.validation.mobileRequired"
			);
		}

		// Airport ID - only validate for internal users
		if (user?.internal_extra && !formData.flughafenausweisnummer?.trim()) {
			newErrors.flughafenausweisnummer = t(
				"profileCompletion.validation.flughafenausweisnummerRequired"
			);
		}

		// Ausweis Color - only validate for internal users
		if (user?.internal_extra && !formData.ausweis_color?.trim()) {
			newErrors.ausweis_color = t(
				"profileCompletion.validation.ausweisfarbeRequired"
			);
		}

		// Occupation - required for external users
		if (user?.internal_extra !== true && !formData.main_occupation_id) {
			newErrors.main_occupation_id = "Beschäftigung ist erforderliche";
		}

		// Occupation Proof - validate for external users with occupation
		// Commented out to allow saving profile without proof upload
		// The profile will remain incomplete on the backend
		// if (
		// 	user?.internal_extra !== true &&
		// 	formData.main_occupation_id &&
		// 	!proofInfo
		// ) {
		// 	newErrors.main_occupation_id = t(
		// 		"profileCompletion.validation.occupationProofRequired"
		// 	);
		// }

		// Address Fields - Required for external users
		if (user?.internal_extra !== true) {
			if (!formData.street?.trim()) {
				newErrors.street = "Straße ist erforderlich";
			}
			if (!formData.postcode?.trim()) {
				newErrors.postcode = "Postleitzahl ist erforderlich";
			}
			if (!formData.city?.trim()) {
				newErrors.city = "Stadt ist erforderlich";
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Check if there are unsaved changes by comparing current form data with original data
	const hasUnsavedChanges = () => {
		if (!extra) {
			return true; // If we don't have original data, assume there are changes
		}

		// Compare key fields that might have changed
		const fieldsToCheck = [
			"first_name",
			"last_name",
			"date_of_birth",
			"nationality",
			"language_level",
			"amount_flights",
			"company",
			"department",
			"flughafenausweisnummer",
			"ausweis_color",
			"main_occupation_id",
		];

		for (const field of fieldsToCheck) {
			const currentValue = formData[field as keyof typeof formData];
			const originalValue = extra[field as keyof typeof extra];

			// Handle date formatting comparison
			if (field === "date_of_birth") {
				const currentDate = currentValue || "";
				const originalDate = originalValue
					? formatDateForInput(originalValue as string)
					: "";
				if (currentDate !== originalDate) {
					return true;
				}
			} else {
				// Handle null/undefined comparisons properly
				const normalizedCurrent =
					currentValue === null || currentValue === undefined
						? ""
						: currentValue;
				const normalizedOriginal =
					originalValue === null || originalValue === undefined
						? ""
						: originalValue;

				if (normalizedCurrent !== normalizedOriginal) {
					return true;
				}
			}
		}

		return false;
	};

	// Check if occupation proof upload should be shown (only for students)
	const shouldShowOccupationProof = () => {
		if (!formData.main_occupation_id) return false;

		// Get the selected occupation
		const selectedOccupation = occupations.find(
			(occ) => occ.id === formData.main_occupation_id
		);

		// Show occupation proof for students and high school students only
		if (selectedOccupation?.name) {
			const occupationName = selectedOccupation.name.toLowerCase();
			return (
				occupationName.includes("student") || occupationName.includes("schüler")
			);
		}

		return false;
	};

	// Navigate to occupation proof
	const navigateToOccupationProof = () => {
		// If no unsaved changes and occupation is selected, navigate directly
		if (!hasUnsavedChanges() && formData.main_occupation_id) {
			navigateDirectly("occupation-proof");
		} else {
			// Show confirmation popup if there are unsaved changes
			setNavigationTarget("occupation-proof");
			setShowNavigationWarningModal(true);
		}
	};

	// Check if work permit upload should be shown (only for non-EU nationals)
	const shouldShowWorkPermitProof = () => {
		if (!formData.nationality) return false;

		// Get the selected country
		const selectedCountry = countries.find(
			(country) => country.country_id === formData.nationality
		);

		// Show work permit proof for non-EU nationals only
		return selectedCountry && !selectedCountry.is_eu;
	};

	// Navigate to work permit proof
	const navigateToWorkPermitProof = () => {
		// If no unsaved changes and nationality is selected, navigate directly
		if (!hasUnsavedChanges() && formData.nationality) {
			navigateDirectly("work-permit-proof");
		} else {
			// Show confirmation popup if there are unsaved changes
			setNavigationTarget("work-permit-proof");
			setShowNavigationWarningModal(true);
		}
	};

	// Handle save and navigate to occupation proof
	const handleSaveAndNavigate = async () => {
		// Validate and save the form
		if (validateForm()) {
			// Filter out email and mobile_number as they should not be editable
			const { email, mobile_number, ...editableFormData } = formData;
			const dataToSubmit = editableFormData;

			if (!user?.extraId) {
				console.error("User extraId is not set");
				setShowNavigationWarningModal(false);
				return;
			}

			// Save the form data
			const updatedExtra = await updateExtra(user?.extraId, dataToSubmit);

			if (updatedExtra) {
				// Update user profile if needed
				if (userProfile) {
					setUserProfile({
						...userProfile,
						firstName: formData.first_name || "",
						lastName: formData.last_name || "",
						phone: formData.mobile_number || "",
						dateOfBirth: formData.date_of_birth || "",
						nationality: formData.nationality
							? countries.find((c) => c.country_id === formData.nationality)
									?.nationality || ""
							: "",
						germanLevel: formData.language_level || "",
						street: formData.street || "",
						postalCode: formData.postcode || "",
						city: formData.city || "",
						cityOfBirth: formData.city_of_birth || "",
						company: formData.company || "",
						department: formData.department || "",
					});
				}

				// Mark profile as complete
				updateUserCompletionState({
					profile_complete: true,
				});

				// Refresh the backend data to sync the extra state
				await refetch();

				// Close the modal and navigate
				setShowNavigationWarningModal(false);

				// Small delay to allow state updates, then navigate
				setTimeout(() => {
					// Navigate based on the saved target
					if (navigationTarget === "work-permit-proof") {
						navigateDirectly("work-permit-proof");
					} else if (navigationTarget === "occupation-proof") {
						navigateDirectly("occupation-proof");
					}
					// If navigationTarget is null, we don't navigate anywhere
				}, 500);
			}
		} else {
			// If form is not valid, show errors and close modal
			setShowNavigationWarningModal(false);
			// Scroll to first error field after a brief delay
			setTimeout(() => {
				const firstErrorField = document.querySelector(
					'[data-has-error="true"]'
				);
				if (firstErrorField) {
					firstErrorField.scrollIntoView({
						behavior: "smooth",
						block: "center",
					});
				}
			}, 100);
		}
	};

	// Handle confirmed occupation change
	const handleConfirmOccupationChange = async () => {
		// Delete the existing proof if it exists
		if (proofInfo) {
			try {
				await deleteProof();
			} catch (error) {
				console.error("Failed to delete occupation proof:", error);
				// Continue with occupation change even if proof deletion fails
			}
		}

		// Update the occupation
		setFormData((prev) => ({
			...prev,
			main_occupation_id: pendingOccupationId,
		}));
		setShowOccupationChangeModal(false);
		setPendingOccupationId(undefined);

		// Clear any occupation-related errors
		if (errors.main_occupation_id) {
			setErrors((prev) => ({ ...prev, main_occupation_id: "" }));
		}
	};

	// Handle cancelled occupation change
	const handleCancelOccupationChange = () => {
		setShowOccupationChangeModal(false);
		setPendingOccupationId(undefined);
	};

	// Handle confirmed nationality change
	const handleConfirmNationalityChange = async () => {
		// Delete the existing work permit if it exists
		if (workPermitInfo) {
			try {
				await deleteWorkPermitProof();
			} catch (error) {
				console.error("Failed to delete work permit proof:", error);
				// Continue with nationality change even if proof deletion fails
			}
		}

		// Update the nationality
		setFormData((prev) => ({
			...prev,
			nationality: pendingNationalityId,
		}));
		setShowNationalityChangeModal(false);
		setPendingNationalityId(undefined);

		// Clear any nationality-related errors
		if (errors.nationality) {
			setErrors((prev) => ({ ...prev, nationality: "" }));
		}
	};

	// Handle cancelled nationality change
	const handleCancelNationalityChange = () => {
		setShowNationalityChangeModal(false);
		setPendingNationalityId(undefined);
	};

	const handleOverlayClose = () => {
		setProfileState((prev: any) => ({
			...prev,
			hasSeenIntro: true,
			showIntroOverlay: false,
			isFirstVisit: false,
		}));
		localStorage.setItem("hasSeenProfileIntro", "true");
	};

	const handleHelpIconClick = () => {
		setProfileState((prev: any) => ({
			...prev,
			showIntroOverlay: true,
		}));
	};

	const handleBackClick = () => {
		// Navigate back directly without showing modal
		// If we're in onboarding flow step 1, go back to onboarding welcome
		// if (onboardingState.currentOnboardingStep === 1) {
		// 	navigateDirectly("onboarding-welcome");
		// } else if (navigationContext?.sourceScreen) {
		// 	navigateDirectly(navigationContext.sourceScreen);
		// } else {
		navigateDirectly("app-profile-completion-overview");
		// }
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		// Validate form and show errors if any
		if (!validateForm()) {
			// Scroll to first error field after a brief delay to allow error states to render
			setTimeout(() => {
				const firstErrorField = document.querySelector(
					'[data-has-error="true"]'
				);
				if (firstErrorField) {
					firstErrorField.scrollIntoView({
						behavior: "smooth",
						block: "center",
					});
				}
			}, 100);
			return;
		}

		// Filter out email and mobile_number as they should not be editable
		const { email, mobile_number, ...editableFormData } = formData;
		const dataToSubmit = editableFormData;

		if (!user?.extraId) {
			console.error("User extraId is not set");
			return;
		}

		// Send only editable form data to API - notifications are handled automatically
		const updatedExtra = await updateExtra(user?.extraId, dataToSubmit);

		if (updatedExtra) {
			// Update was successful
			if (userProfile) {
				// Map the Extra data to UserProfile format if needed
				setUserProfile({
					...userProfile,
					firstName: formData.first_name || "",
					lastName: formData.last_name || "",
					phone: formData.mobile_number || "",
					dateOfBirth: formData.date_of_birth || "",
					nationality: formData.nationality
						? countries.find((c) => c.country_id === formData.nationality)
								?.nationality || ""
						: "",
					germanLevel: formData.language_level || "",
					street: formData.street || "",
					postalCode: formData.postcode || "",
					city: formData.city || "",
					cityOfBirth: formData.city_of_birth || "",
					company: formData.company || "",
					department: formData.department || "",
				});
			}

			// Mark profile as complete
			updateUserCompletionState({
				profile_complete: true,
			});

			// Refresh the backend data to sync the extra state
			await refetch();

			// Navigate back to profile overview screen after successful save
			navigateDirectly("app-profile-completion-overview");
		}
	};

	const handleRefreshData = async () => {
		setIsLoadingData(true);
		await refetch();
	};

	// Check access requirements
	const accessCheck = canAccessScreen("profile-data");
	const hasRequirements =
		accessCheck.requirements && accessCheck.requirements.length > 0;

	// Show loading state
	if (isLoadingData || apiLoading || countriesLoading || occupationsLoading) {
		return (
			<MainLayout showBack={true} onBack={handleBackClick}>
				<div className="profile-completion-screen">
					<div className="flex items-center justify-between mb-6">
						<button className="p-2" onClick={handleHelpIconClick}>
							<HelpCircle className="w-5 h-5 text-[var(--ucm-text-medium)]" />
						</button>
					</div>

					<div className="flex flex-col items-center justify-center min-h-[400px]">
						<Loader2 className="w-8 h-8 animate-spin text-[var(--ucm-primary-teal)]" />
						<p className="mt-4 text-[var(--ucm-text-medium)]">
							{t("common.loading")}
						</p>
					</div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack={true} onBack={handleBackClick}>
			<div className="profile-completion-screen">
				{/* Header */}
				<div className="flex items-center justify-between mb-4">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)]">
						{isProfileComplete
							? t("common.profile")
							: t("profileCompletion.title")}
					</h1>
					<button className="p-2" onClick={handleHelpIconClick}>
						<HelpCircle className="w-5 h-5 text-[var(--ucm-text-medium)]" />
					</button>
				</div>

				<div className="profile-form-content">
					<div className="mb-6">
						<p className="text-[var(--ucm-text-medium)] text-left">
							{t("profileCompletion.subtitle")}
						</p>
					</div>

					{/* API Error Notice */}
					{apiError && (
						<div className="bg-[var(--ucm-light-yellow)] border border-[var(--ucm-warning-yellow)] rounded-2xl p-4 mb-6">
							<div className="flex items-start space-x-3">
								<span className="text-xl">⚠️</span>
								<div className="flex-1">
									<p className="text-sm text-[var(--ucm-dark-yellow)]">
										{t("profileCompletion.unableToLoadData")}
									</p>
									<button
										onClick={handleRefreshData}
										className="mt-2 text-sm text-[var(--ucm-primary-teal)] hover:underline"
									>
										{t("profileCompletion.tryAgain")}
									</button>
								</div>
							</div>
						</div>
					)}

					{/* Requirements Notice */}
					{hasRequirements && (
						<div className="bg-[var(--ucm-light-yellow)] border border-[var(--ucm-warning-yellow)] rounded-2xl p-4 mb-6">
							<div className="flex items-start space-x-3">
								<span className="text-xl">⚠️</span>
								<div>
									<h3 className="font-semibold text-[var(--ucm-dark-yellow)] mb-2">
										{t("profileCompletion.recommendedPrerequisites")}
									</h3>
									<ul className="space-y-1 text-sm text-[var(--ucm-dark-yellow)] mb-3">
										{accessCheck.requirements!.map((req, index) => (
											<li key={index} className="flex items-center">
												<span className="w-4 h-4 border border-[var(--ucm-warning-yellow)] rounded-full text-xs flex items-center justify-center mr-2">
													!
												</span>
												{req}
											</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					)}

					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Personal Information Section */}
						<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
							<h2 className="font-semibold text-[var(--ucm-text-dark)] mb-4">
								{t("profileCompletion.personalInfo")}
							</h2>

							<div className="space-y-4">
								{/* Email (read-only) */}
								<div>
									<label
										htmlFor="email"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.email")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<input
										type="email"
										id="email"
										value={formData.email || ""}
										disabled={true}
										className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl bg-[var(--ucm-disabled-gray)] cursor-not-allowed"
									/>
								</div>

								{/* First Name */}
								<div>
									<label
										htmlFor="firstName"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.firstName")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="firstName"
										value={formData.first_name || ""}
										onChange={(e) =>
											handleInputChange("first_name", e.target.value)
										}
										data-has-error={errors.first_name ? "true" : "false"}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
											errors.first_name
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
										placeholder={t(
											"profileCompletion.fields.firstNamePlaceholder"
										)}
									/>
									{errors.first_name && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.first_name}
										</p>
									)}
								</div>

								{/* Last Name */}
								<div>
									<label
										htmlFor="lastName"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.lastName")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<input
										type="text"
										id="lastName"
										value={formData.last_name || ""}
										onChange={(e) =>
											handleInputChange("last_name", e.target.value)
										}
										data-has-error={errors.last_name ? "true" : "false"}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
											errors.last_name
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
										placeholder={t(
											"profileCompletion.fields.lastNamePlaceholder"
										)}
									/>
									{errors.last_name && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.last_name}
										</p>
									)}
								</div>

								{/* Phone Number (read-only) */}
								<div>
									<label
										htmlFor="phone"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.phoneNumber")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<input
										type="tel"
										id="phone"
										value={formData.mobile_number || ""}
										disabled={true}
										className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl bg-[var(--ucm-disabled-gray)] cursor-not-allowed"
										placeholder="+49 123 456 7890"
									/>
								</div>

								{/* Date of Birth */}
								<div>
									<label
										htmlFor="dateOfBirth"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.dateOfBirth")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<input
										type="date"
										id="dateOfBirth"
										value={formData.date_of_birth || ""}
										min="1900-01-01"
										max={new Date().toISOString().split("T")[0]} // Prevent selecting future dates
										onChange={(e) =>
											handleInputChange("date_of_birth", e.target.value)
										}
										data-has-error={errors.date_of_birth ? "true" : "false"}
										className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
											errors.date_of_birth
												? "border-[var(--ucm-error-red)]"
												: "border-[var(--ucm-border-light)]"
										}`}
									/>
									{errors.date_of_birth && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.date_of_birth}
										</p>
									)}
								</div>

								{/* Nationality */}
								<div>
									<label
										htmlFor="nationality"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.nationality")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<div className="relative">
										<select
											id="nationality"
											value={formData.nationality || ""}
											onChange={(e) =>
												handleInputChange(
													"nationality",
													e.target.value ? parseInt(e.target.value) : undefined
												)
											}
											data-has-error={errors.nationality ? "true" : "false"}
											className={`w-full px-4 py-3 pr-12 border rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
												errors.nationality
													? "border-[var(--ucm-error-red)]"
													: "border-[var(--ucm-border-light)]"
											}`}
										>
											<option value="">
												{t("profileCompletion.fields.nationalityPlaceholder")}
											</option>
											{countries.map((country) => (
												<option
													key={country.country_id}
													value={country.country_id}
												>
													{country.nationality}
												</option>
											))}
										</select>
										<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
									</div>
									{errors.nationality && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.nationality}
										</p>
									)}
								</div>

								{/* ID Number - Only for external users */}
								{user?.internal_extra !== true && (
									<div>
										<label
											htmlFor="idNumber"
											className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
										>
											ID-Nummer <span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<input
												type="text"
												id="idNumber"
												value={formData.id_number || ""}
												onChange={(e) =>
													handleInputChange("id_number", e.target.value)
												}
												className={`w-full px-4 pr-10 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
													errors.id_number
														? "border-[var(--ucm-error-red)]"
														: "border-[var(--ucm-border-light)]"
												}`}
												placeholder="Personalausweis- oder Reisepassnummer"
												maxLength={50}
											/>
											<Info
												className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--ucm-text-light)] cursor-pointer hover:text-[var(--ucm-primary-teal)] transition-colors"
												onClick={() => setShowIdInfoModal(true)}
											/>
										</div>
										{errors.id_number && (
											<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
												{errors.id_number}
											</p>
										)}
									</div>
								)}

								{/* Work permit proof button - Only for non-EU nationals */}
								{user?.internal_extra !== true &&
									shouldShowWorkPermitProof() && (
										<div className="mt-3">
											<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
												Arbeitserlaubnis <span className="text-red-500">*</span>
											</label>
											<div
												onClick={navigateToWorkPermitProof}
												className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl cursor-pointer hover:border-[var(--ucm-primary-teal)] transition-colors flex items-center justify-between group"
											>
												<div className="flex items-center gap-3">
													{workPermitInfo ? (
														<>
															<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
															<span className="text-[var(--ucm-text-dark)]">
																{workPermitInfo.file_name}
															</span>
														</>
													) : (
														<>
															<Upload className="w-5 h-5 text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]" />
															<span className="text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]">
																Arbeitserlaubnis hochladen
															</span>
														</>
													)}
												</div>

												<ChevronDown className="w-5 h-5 text-[var(--ucm-text-medium)] transform -rotate-90" />
											</div>

											{!workPermitInfo ? (
												<p className="mt-2 text-xs text-[var(--ucm-text-light)]">
													Als Nicht-EU-Bürger benötigen wir Ihre
													Arbeitserlaubnis. Bitte laden Sie den Nachweis hoch,
													um sich auf Events bewerben zu können.
												</p>
											) : (
												<div
													className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
														workPermitInfo.status === "pending"
															? "bg-yellow-100 text-yellow-800"
															: workPermitInfo.status === "approved"
																? "bg-green-100 text-green-800"
																: "bg-red-100 text-red-800"
													}`}
												>
													{workPermitInfo.status === "approved" && (
														<CheckCircle className="w-3 h-3 text-green-800" />
													)}

													{workPermitInfo.status === "pending" && (
														<Clock className="w-3 h-3 text-yellow-800" />
													)}
													{workPermitInfo.status === "rejected" && (
														<XCircle className="w-3 h-3 text-red-800" />
													)}

													{workPermitInfo.status === "pending" && "Ausstehend"}
													{workPermitInfo.status === "approved" && "Genehmigt"}
													{workPermitInfo.status === "rejected" && "Abgelehnt"}
												</div>
											)}
										</div>
									)}

								{/* German Level */}
								<div>
									<label
										htmlFor="germanLevel"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										{t("profileCompletion.fields.germanLevel")}{" "}
										<span className="text-red-500">*</span>
									</label>
									<div className="relative">
										<select
											id="germanLevel"
											value={formData.language_level || ""}
											onChange={(e) =>
												handleInputChange(
													"language_level",
													e.target.value as LanguageLevel
												)
											}
											data-has-error={errors.language_level ? "true" : "false"}
											className={`w-full px-4 py-3 pr-12 border rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
												errors.language_level
													? "border-[var(--ucm-error-red)]"
													: "border-[var(--ucm-border-light)]"
											}`}
										>
											<option value="">
												{t("profileCompletion.fields.germanLevelPlaceholder")}
											</option>
											{LANGUAGE_LEVELS.map((level) => (
												<option key={level} value={level}>
													{level}
												</option>
											))}
										</select>
										<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
									</div>
									{errors.language_level && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.language_level}
										</p>
									)}
								</div>

								{/* Amount of Flights - Required for all users */}
								<div>
									<label
										htmlFor="amountFlights"
										className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
									>
										Anzahl der Flüge im letzten Jahr{" "}
										<span className="text-red-500">*</span>
									</label>
									<div className="relative">
										<select
											id="amountFlights"
											value={formData.amount_flights || ""}
											onChange={(e) =>
												handleInputChange(
													"amount_flights",
													e.target.value as AmountFlights
												)
											}
											data-has-error={errors.amount_flights ? "true" : "false"}
											className={`w-full px-4 py-3 pr-12 border rounded-xl appearance-none focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
												errors.amount_flights
													? "border-[var(--ucm-error-red)]"
													: "border-[var(--ucm-border-light)]"
											}`}
										>
											<option value="">Bitte wählen</option>
											{AMOUNT_FLIGHTS.map((amount) => (
												<option key={amount} value={amount}>
													{amount} Flüge
												</option>
											))}
										</select>
										<ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] w-5 h-5 pointer-events-none" />
									</div>
									{errors.amount_flights && (
										<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.amount_flights}
										</p>
									)}
								</div>

								{/* Company - Only show for internal users */}
								{user?.internal_extra && (
									<div>
										<label
											htmlFor="company"
											className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
										>
											{t("profileCompletion.fields.company")}
										</label>
										<div className="relative">
											<select
												id="company"
												value={formData.company || ""}
												onChange={(e) =>
													handleInputChange("company", e.target.value)
												}
												className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] appearance-none pr-10"
											>
												<option value="">
													{t("profileCompletion.fields.companyPlaceholder")}
												</option>
												{COMPANY_OPTIONS.map((company) => (
													<option key={company} value={company}>
														{company}
													</option>
												))}
											</select>
											<ChevronDown
												className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
												size={20}
											/>
										</div>
									</div>
								)}

								{/* Department - Only show for internal users */}
								{user?.internal_extra && (
									<div>
										<label
											htmlFor="department"
											className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
										>
											{t("profileCompletion.fields.department")}
										</label>
										<input
											type="text"
											id="department"
											value={formData.department || ""}
											onChange={(e) =>
												handleInputChange("department", e.target.value)
											}
											className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)]"
											placeholder={t(
												"profileCompletion.fields.departmentPlaceholder"
											)}
										/>
									</div>
								)}

								{/* Airport ID - Only show for internal users */}
								{user?.internal_extra && (
									<div>
										<label
											htmlFor="flughafenausweisnummer"
											className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
										>
											{t("profileCompletion.fields.flughafenausweisnummer")}{" "}
											<span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											id="flughafenausweisnummer"
											value={formData.flughafenausweisnummer}
											onChange={(e) =>
												handleInputChange(
													"flughafenausweisnummer",
													e.target.value
												)
											}
											data-has-error={
												errors.flughafenausweisnummer ? "true" : "false"
											}
											className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
												errors.flughafenausweisnummer
													? "border-[var(--ucm-error-red)]"
													: "border-[var(--ucm-border-light)]"
											}`}
											placeholder={t(
												"profileCompletion.fields.flughafenausweisnummerPlaceholder"
											)}
											maxLength={50}
										/>
										{errors.flughafenausweisnummer && (
											<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
												{errors.flughafenausweisnummer}
											</p>
										)}
									</div>
								)}

								{/* Ausweis Color - Only show for internal users */}
								{user?.internal_extra && (
									<div>
										<label
											htmlFor="ausweisColor"
											className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
										>
											{t("profileCompletion.fields.ausweisColor")}{" "}
											<span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<select
												id="ausweisColor"
												value={formData.ausweis_color}
												onChange={(e) =>
													handleInputChange("ausweis_color", e.target.value)
												}
												data-has-error={errors.ausweis_color ? "true" : "false"}
												className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] appearance-none pr-10 ${
													errors.ausweis_color
														? "border-[var(--ucm-error-red)]"
														: "border-[var(--ucm-border-light)]"
												}`}
											>
												<option value="">
													{t(
														"profileCompletion.fields.ausweisColorPlaceholder"
													)}
												</option>
												{AUSWEIS_COLOR_OPTIONS.map((color) => (
													<option key={color} value={color}>
														{color}
													</option>
												))}
											</select>
											<ChevronDown
												className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
												size={20}
											/>
										</div>
										{errors.ausweis_color && (
											<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
												{errors.ausweis_color}
											</p>
										)}
									</div>
								)}

								{/* Occupation - Only show for external users */}
								{user?.internal_extra !== true && (
									<div>
										<label
											htmlFor="occupation"
											className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
										>
											{t("profileCompletion.fields.occupation")}{" "}
											<span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<select
												id="occupation"
												value={formData.main_occupation_id || ""}
												onChange={(e) =>
													handleInputChange(
														"main_occupation_id",
														e.target.value
															? parseInt(e.target.value)
															: undefined
													)
												}
												data-has-error={
													errors.main_occupation_id ? "true" : "false"
												}
												className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] appearance-none pr-10 ${
													errors.main_occupation_id
														? "border-[var(--ucm-error-red)]"
														: "border-[var(--ucm-border-light)]"
												}`}
											>
												<option value="">
													{t("profileCompletion.fields.occupationPlaceholder")}
												</option>
												{occupationsLoading ? (
													<option disabled>Loading...</option>
												) : (
													occupations.map((occupation) => (
														<option key={occupation.id} value={occupation.id}>
															{occupation.name}
														</option>
													))
												)}
											</select>
											<ChevronDown
												className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--ucm-text-light)] pointer-events-none"
												size={20}
											/>
										</div>
										{errors.main_occupation_id && (
											<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
												{errors.main_occupation_id}
											</p>
										)}
									</div>
								)}

								{/* Address Fields - Always show for external users */}
								{user?.internal_extra !== true && (
										<div>
											{/* Street */}
											<div>
												<label
													htmlFor="street"
													className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
												>
													Straße und Hausnummer{" "}
													<span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													id="street"
													value={formData.street || ""}
													onChange={(e) =>
														handleInputChange("street", e.target.value)
													}
													data-has-error={errors.street ? "true" : "false"}
													className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
														errors.street
															? "border-[var(--ucm-error-red)]"
															: "border-[var(--ucm-border-light)]"
													}`}
													placeholder="z.B. Musterstraße 123"
													maxLength={255}
												/>
												{errors.street && (
													<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
														{errors.street}
													</p>
												)}
											</div>

											{/* Postcode */}
											<div>
												<label
													htmlFor="postcode"
													className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2 mt-4"
												>
													Postleitzahl <span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													id="postcode"
													value={formData.postcode || ""}
													onChange={(e) =>
														handleInputChange("postcode", e.target.value)
													}
													data-has-error={errors.postcode ? "true" : "false"}
													className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
														errors.postcode
															? "border-[var(--ucm-error-red)]"
															: "border-[var(--ucm-border-light)]"
													}`}
													placeholder="z.B. 12345"
													maxLength={10}
												/>
												{errors.postcode && (
													<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
														{errors.postcode}
													</p>
												)}
											</div>

											{/* City */}
											<div>
												<label
													htmlFor="city"
													className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2 mt-4"
												>
													Stadt <span className="text-red-500">*</span>
												</label>
												<input
													type="text"
													id="city"
													value={formData.city || ""}
													onChange={(e) =>
														handleInputChange("city", e.target.value)
													}
													data-has-error={errors.city ? "true" : "false"}
													className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] ${
														errors.city
															? "border-[var(--ucm-error-red)]"
															: "border-[var(--ucm-border-light)]"
													}`}
													placeholder="z.B. Frankfurt"
													maxLength={255}
												/>
												{errors.city && (
													<p className="mt-1 text-sm text-[var(--ucm-error-red)]">
														{errors.city}
													</p>
												)}
											</div>

										</div>
									)}

								{/* Occupation proof button - Only for students */}
								{user?.internal_extra !== true &&
									shouldShowOccupationProof() && (
										<div className="mt-3">
											<label className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2">
												{t("profileCompletion.occupationProof")}{" "}
												<span className="text-red-500">*</span>
											</label>
											<div
												onClick={navigateToOccupationProof}
												className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl cursor-pointer hover:border-[var(--ucm-primary-teal)] transition-colors flex items-center justify-between group"
											>
												<div className="flex items-center gap-3">
													{proofInfo ? (
														<>
															<FileText className="w-5 h-5 text-[var(--ucm-primary-teal)]" />
															<span className="text-[var(--ucm-text-dark)]">
																{proofInfo.file_name}
															</span>
														</>
													) : (
														<>
															<Upload className="w-5 h-5 text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]" />
															<span className="text-[var(--ucm-text-medium)] group-hover:text-[var(--ucm-primary-teal)]">
																{t("profileCompletion.uploadOccupationProof")}
															</span>
														</>
													)}
												</div>
												<ChevronDown className="w-5 h-5 text-[var(--ucm-text-medium)] transform -rotate-90" />
											</div>
											{!proofInfo ? (
												<p className="mt-2 text-xs text-[var(--ucm-text-light)]">
													Bitte laden Sie den Nachweis hoch, um Termine
													auswählen zu können.
												</p>
											) : (
												<div
													className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1 ${
														proofInfo.status === "pending"
															? "bg-yellow-100 text-yellow-800"
															: proofInfo.status === "approved"
																? "bg-green-100 text-green-800"
																: "bg-red-100 text-red-800"
													}`}
												>
													{proofInfo.status === "approved" && (
														<CheckCircle className="w-3 h-3 text-green-800" />
													)}

													{proofInfo.status === "pending" && (
														<Clock className="w-3 h-3 text-yellow-800" />
													)}
													{proofInfo.status === "rejected" && (
														<XCircle className="w-3 h-3 text-red-800" />
													)}

													{proofInfo.status === "pending" && "Ausstehend"}
													{proofInfo.status === "approved" && "Genehmigt"}
													{proofInfo.status === "rejected" && "Abgelehnt"}
												</div>
											)}
										</div>
									)}
							</div>
						</div>

						{/* Agreement Checkboxes - Only for external users */}
						{user?.internal_extra !== true && (
							<div className="space-y-4 mb-6">
								<div>
									<div className="flex items-center space-x-3">
										<input
											type="checkbox"
											id="accepts64DayRule"
											checked={formData.accepts_64_day_rule || false}
											onChange={(e) =>
												handleInputChange(
													"accepts_64_day_rule",
													e.target.checked
												)
											}
											className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
										/>
										<label
											htmlFor="accepts64DayRule"
											className="text-sm leading-5 text-[var(--ucm-text-dark)] flex items-center gap-2"
										>
											<span>
												Ich werde zum Zeitpunkt des Probebetriebs noch keine 64
												Tage in kurzfristiger Beschäftigung absolviert haben.{" "}
												<span className="text-red-500">*</span>
											</span>
											<Info
												className="w-4 h-4 text-[var(--ucm-info-blue)] cursor-help flex-shrink-0"
												onClick={() => setShow64DayRuleInfoModal(true)}
											/>
										</label>
									</div>
									{errors.accepts_64_day_rule && (
										<p className="ml-8 mt-1 text-sm text-[var(--ucm-error-red)]">
											{errors.accepts_64_day_rule}
										</p>
									)}
								</div>
								<div className="flex items-center space-x-3">
									<input
										type="checkbox"
										id="acknowledgesDataAccuracy"
										checked={acknowledgesDataAccuracy}
										onChange={(e) =>
											setAcknowledgesDataAccuracy(e.target.checked)
										}
										className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
									/>
									<label
										htmlFor="acknowledgesDataAccuracy"
										className="text-sm leading-5 text-[var(--ucm-text-dark)]"
									>
										Ich bestätige, dass die obigen Angaben nach bestem Wissen
										korrekt sind. Bei Änderungen werde ich diese unverzüglich
										aktualisieren. <span className="text-red-500">*</span>
									</label>
								</div>
								<div className="flex items-center space-x-3">
									<input
										type="checkbox"
										id="confirmedUnallowedOccupation"
										checked={formData.confirmed_unallowed_occupation || false}
										onChange={(e) =>
											handleInputChange(
												"confirmed_unallowed_occupation",
												e.target.checked
											)
										}
										className="w-5 h-5 text-[var(--ucm-primary-teal)] border-[var(--ucm-border-light)] rounded focus:ring-[var(--ucm-primary-teal)] flex-shrink-0"
									/>
									<label
										htmlFor="confirmedUnallowedOccupation"
										className="text-sm leading-5 text-[var(--ucm-text-dark)]"
									>
										Ich bestätige, dass ich zum Zeitpunkt des Probebetriebs
										weder in <strong>Elternzeit</strong> noch in{" "}
										<strong>unbezahltem Urlaub</strong> oder{" "}
										<strong>arbeitslos</strong> sein werde und meine oben
										angegebene Hauptbeschäftigung weiterhin ausübe.{" "}
										<span className="text-red-500">*</span>
									</label>
								</div>
							</div>
						)}

						{/* Button Section */}
						<div className="space-y-3">
							{/* Submit Button */}
							<button
								type="submit"
								disabled={
									updateLoading ||
									(user?.internal_extra !== true &&
										(!formData.accepts_64_day_rule ||
											!acknowledgesDataAccuracy ||
											!formData.confirmed_unallowed_occupation))
								}
								className={`w-full h-12 font-semibold rounded-full transition-colors duration-200 flex items-center justify-center ${
									!updateLoading &&
									(user?.internal_extra === true ||
										(formData.accepts_64_day_rule && acknowledgesDataAccuracy && formData.confirmed_unallowed_occupation))
										? "bg-[var(--ucm-primary-teal)] text-white hover:bg-[var(--ucm-primary-teal-hover)]"
										: "bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] cursor-not-allowed"
								}`}
							>
								{updateLoading ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin mr-2" />
										{t("profileCompletion.saving")}
									</>
								) : (
									t("profileCompletion.saveProfile")
								)}
							</button>

							{/* Back Button */}
							<button
								type="button"
								onClick={() =>
									navigateDirectly("app-profile-completion-overview")
								}
								className="w-full h-12 font-semibold rounded-full border border-[var(--ucm-border-medium)] text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
							>
								Zurück
							</button>
						</div>
					</form>
				</div>

				{/* Overlay Component */}
				<ProfileIntroOverlay
					isVisible={profileState.showIntroOverlay}
					onClose={handleOverlayClose}
					isFirstTime={profileState.isFirstVisit}
				/>

				{/* Navigation Warning Modal */}
				{showNavigationWarningModal && (
					<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
						<div className="bg-white rounded-2xl p-6 max-w-md w-full">
							<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
								Änderungen speichern?
							</h2>
							<p className="text-[var(--ucm-text-medium)] mb-6">
								Sie haben ungespeicherte Änderungen. Möchten Sie diese
								speichern, bevor Sie zur{" "}
								{shouldShowWorkPermitProof() && !shouldShowOccupationProof()
									? "Arbeitserlaubnis"
									: "Beschäftigungsnachweis"}
								-Upload Seite navigieren?
							</p>
							<div className="flex gap-3">
								<button
									onClick={() => setShowNavigationWarningModal(false)}
									className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
								>
									{t("common.cancel")}
								</button>
								<button
									onClick={handleSaveAndNavigate}
									disabled={updateLoading}
									className="flex-1 px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-[var(--ucm-primary-teal-hover)] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
								>
									{updateLoading ? (
										<span className="flex items-center justify-center">
											<Loader2 className="w-4 h-4 animate-spin mr-2" />
											Speichern...
										</span>
									) : (
										"Speichern und fortfahren"
									)}
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Occupation Change Confirmation Modal */}
				{showOccupationChangeModal && (
					<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
						<div className="bg-white rounded-2xl p-6 max-w-md w-full">
							<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
								Beschäftigung ändern
							</h2>
							<p className="text-[var(--ucm-text-medium)] mb-6">
								Wenn Sie Ihre Beschäftigung ändern, wird Ihr vorhandener
								Beschäftigungsnachweis gelöscht. Sie müssen einen neuen Nachweis
								für den ausgewählten Beschäftigung hochladen.
							</p>
							<div className="flex gap-3">
								<button
									onClick={handleCancelOccupationChange}
									className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
								>
									{t("common.cancel")}
								</button>
								<button
									onClick={handleConfirmOccupationChange}
									className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
								>
									Änderung bestätigen
								</button>
							</div>
						</div>
					</div>
				)}

				{/* Nationality Change Confirmation Modal */}
				{showNationalityChangeModal && (
					<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
						<div className="bg-white rounded-2xl p-6 max-w-md w-full">
							<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
								Nationalität ändern
							</h2>
							<p className="text-[var(--ucm-text-medium)] mb-6">
								Wenn Sie Ihre Nationalität ändern, wird Ihre vorhandene
								Arbeitserlaubnis gelöscht.{" "}
								{pendingNationalityId &&
								countries.find((c) => c.country_id === pendingNationalityId)
									?.is_eu
									? "Als EU-Bürger benötigen Sie keine Arbeitserlaubnis mehr."
									: "Sie müssen eine neue Arbeitserlaubnis für die ausgewählte Nationalität hochladen."}
							</p>
							<div className="flex gap-3">
								<button
									onClick={handleCancelNationalityChange}
									className="flex-1 px-4 py-2 border border-[var(--ucm-border-medium)] rounded-xl text-[var(--ucm-text-dark)] hover:bg-gray-50 transition-colors"
								>
									{t("common.cancel")}
								</button>
								<button
									onClick={handleConfirmNationalityChange}
									className="flex-1 px-4 py-2 bg-[var(--ucm-warning-orange)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
								>
									Änderung bestätigen
								</button>
							</div>
						</div>
					</div>
				)}

				{/* ID Number Info Modal */}
				{showIdInfoModal && (
					<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
						<div className="bg-white rounded-2xl p-6 max-w-md w-full">
							<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
								Hinweis zur ID-Nummer
							</h2>
							<div className="space-y-4 text-[var(--ucm-text-medium)]">
								<p>
									Sie können hier jede gültige Ausweisnummer eingeben
									(Personalausweis oder Reisepass). Der deutsche Personalausweis
									wird jedoch bevorzugt.
								</p>
								<p className="font-medium">
									Wichtig: Bitte bringen Sie zum Probebetrieb genau das Dokument
									mit, dessen Nummer Sie hier eingeben. Dies wird zur
									Verifizierung benötigt.
								</p>
							</div>
							<div className="mt-6">
								<button
									onClick={() => setShowIdInfoModal(false)}
									className="w-full px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
								>
									Verstanden
								</button>
							</div>
						</div>
					</div>
				)}

				{/* 64-Day Rule Info Modal */}
				{show64DayRuleInfoModal && (
					<div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
						<div className="bg-white rounded-2xl p-6 max-w-md w-full">
							<h2 className="text-xl font-bold text-[var(--ucm-text-dark)] mb-4">
								64-Tage-Regel für kurzfristige Beschäftigung
							</h2>
							<div className="space-y-4 text-[var(--ucm-text-medium)]">
								<p>
									Kurzfristige Beschäftigungen sind grundsätzlich auf maximal{" "}
									<strong>70 Arbeitstage</strong> pro Kalenderjahr begrenzt.
								</p>
								<p>
									Nach Abzug der gesetzlich zustehenden Urlaubstage (6 Tage bei
									einer 5-Tage-Woche) verbleiben <strong>64 Arbeitstage</strong>
									, die Sie im Jahr kurzfristig beschäftigt sein dürfen.
								</p>
								<p className="font-medium">
									Diese Regelung stellt sicher, dass kurzfristige
									Beschäftigungen sozialversicherungsfrei bleiben und gilt für
									alle kurzfristigen Tätigkeiten zusammen, nicht nur für ucm.
								</p>
							</div>
							<div className="mt-6">
								<button
									onClick={() => setShow64DayRuleInfoModal(false)}
									className="w-full px-4 py-2 bg-[var(--ucm-primary-teal)] text-white rounded-xl hover:bg-opacity-90 transition-colors"
								>
									Verstanden
								</button>
							</div>
						</div>
					</div>
				)}
			</div>
		</MainLayout>
	);
}
